import { AsyncMultiSelectField, CharField, LinkField, MultiSelectField, SwitchField } from "@inmagik/magik-ui"
import { FieldArray, getIn, useFormikContext } from "formik"
import { Col, Row } from "react-bootstrap"
import type { Document } from "../../types"
import { WysiwygField } from "../../components/WysiwygField"
import { EVENT_TYPES, RECORD_MEDIA_TYPES } from "../../constants"
import { useGenresLazy, useGenreLazy, useCreateGenre } from "../../hooks/genres"
import useModalTrigger from "magik-react-hooks/useModalTrigger"
import GenreModal from "../Genres/GenreModal"
import { transformErrorForForm } from "../../utils/utils"
import { useCreateNamedEntity, useNamedEntitiesLazy, useNamedEntityLazy } from "../../hooks/named-entities"
import NamedEntityModal from "../NamedEntities/NamedEntityModal"
import { useCollectionLazy, useCollectionsLazy, useCreateCollection } from "../../hooks/collections"
import CollectionModal from "../Collections/CollectionModal"
import { useMunicipalitiesLazy, useMunicipalityLazy } from "../../hooks/municipalities"

export default function DocumentForm({ col = 8 }: { col?: number }) {
  const { setFieldValue, values, errors, submitCount } = useFormikContext<Document>()

  const fetchGenre = useGenreLazy()
  const fetchGenres = useGenresLazy()
  const fetchNamedEntity = useNamedEntityLazy()
  const fetchNamedEntitiesAuthors = useNamedEntitiesLazy({ entity_type: "AUTHOR" })
  const fetchNamedEntitiesProducers = useNamedEntitiesLazy({ entity_type: "PRODUCER" })
  const fetchNamedEntitiesEditors = useNamedEntitiesLazy({ entity_type: "EDITOR" })
  const fetchNamedEntitiesArchives = useNamedEntitiesLazy({ entity_type: "ARCHIVE" })
  const fetchCollection = useCollectionLazy()
  const fetchCollections = useCollectionsLazy()
  const fetchMunicipality = useMunicipalityLazy()
  const fetchMunicipalities = useMunicipalitiesLazy()

  const createGenre = useCreateGenre()
  const createNamedEntity = useCreateNamedEntity()
  const createCollection = useCreateCollection()

  const [modalStateCreateGenre, modalActionsCreateGenre] = useModalTrigger()
  const [modalStateCreateNamedEntity, modalActionsCreateNamedEntity] = useModalTrigger()
  const [modalStateCreateCollection, modalActionsCreateCollection] = useModalTrigger()

  return (
    <Col xs={col} className="d-flex flex-column align-items-stretch gap-4">
      <CharField
        label="Titolo"
        layout="horizontal"
        value={values.title}
        onChange={(value) => {
          setFieldValue("title", value)
        }}
        isValid={submitCount > 0 ? !getIn(errors, "title") : undefined}
        errorMessage={getIn(errors, "title")}
        required
      />
      <WysiwygField
        label="Descrizione"
        layout="horizontal"
        value={values.description || ""}
        onChange={(value) => {
          setFieldValue("description", value)
        }}
        isValid={submitCount > 0 ? !getIn(errors, "description") : undefined}
        errorMessage={getIn(errors, "description")}
        required
      />
      <MultiSelectField
        label="Tipologia"
        layout="horizontal"
        options={RECORD_MEDIA_TYPES}
        value={RECORD_MEDIA_TYPES.filter((type) => values.record_media_types?.includes(type.value))}
        onChange={(value) => {
          setFieldValue(
            "record_media_types",
            value.map((v) => v.value)
          )
        }}
        isValid={submitCount > 0 ? !getIn(errors, "record_media_types") : undefined}
        errorMessage={getIn(errors, "record_media_types")}
        required
      />
      <AsyncMultiSelectField
        label="Generi"
        layout="horizontal"
        placeholder="Seleziona uno o più generi"
        onChange={(value) => {
          setFieldValue("genres", value)
        }}
        clearable
        value={values.genres || []}
        loadInitialValues={(ids: number[]) =>
          Promise.all(ids?.map((id) => fetchGenre(id).then((genre) => ({ label: genre.name, value: genre.id }))))
        }
        loadOptions={(inputValue: string) => {
          return fetchGenres({ search: inputValue }).then((genres) => {
            return genres.results?.map((genre) => ({
              label: genre.name,
              value: genre.id,
            }))
          })
        }}
      >
        <AsyncMultiSelectField.Add
          onClick={() => {
            modalActionsCreateGenre.open()
          }}
        />
      </AsyncMultiSelectField>

      <MultiSelectField
        label="Tipi di evento"
        layout="horizontal"
        options={EVENT_TYPES}
        value={EVENT_TYPES.filter((type) => values.event_types?.includes(type.value))}
        onChange={(value) => {
          setFieldValue(
            "event_types",
            value.map((v) => v.value)
          )
        }}
        isValid={submitCount > 0 ? !getIn(errors, "event_types") : undefined}
        errorMessage={getIn(errors, "event_types")}
        required
      />
      <AsyncMultiSelectField
        label="Autori"
        layout="horizontal"
        placeholder="Seleziona uno o più autori"
        onChange={(value) => {
          setFieldValue("authors", value)
        }}
        clearable
        value={values.authors || []}
        loadInitialValues={(ids: number[]) =>
          Promise.all(
            ids?.map((id) => fetchNamedEntity(id).then((author) => ({ label: author.name, value: author.id })))
          )
        }
        loadOptions={(inputValue: string) => {
          return fetchNamedEntitiesAuthors({ search: inputValue }).then((authors) => {
            return authors.results?.map((author) => ({
              label: author.name,
              value: author.id,
            }))
          })
        }}
      >
        <AsyncMultiSelectField.Add
          onClick={() => {
            modalActionsCreateNamedEntity.open("AUTHOR")
          }}
        />
      </AsyncMultiSelectField>
      <AsyncMultiSelectField
        label="Editori"
        layout="horizontal"
        placeholder="Seleziona uno o più editori"
        onChange={(value) => {
          setFieldValue("editors", value)
        }}
        clearable
        value={values.editors || []}
        loadInitialValues={(ids: number[]) =>
          Promise.all(
            ids?.map((id) => fetchNamedEntity(id).then((editor) => ({ label: editor.name, value: editor.id })))
          )
        }
        loadOptions={(inputValue: string) => {
          return fetchNamedEntitiesEditors({ search: inputValue }).then((editors) => {
            return editors.results?.map((editor) => ({
              label: editor.name,
              value: editor.id,
            }))
          })
        }}
      >
        <AsyncMultiSelectField.Add
          onClick={() => {
            modalActionsCreateNamedEntity.open("EDITOR")
          }}
        />
      </AsyncMultiSelectField>
      <AsyncMultiSelectField
        label="Produttori"
        layout="horizontal"
        placeholder="Seleziona uno o più produttori"
        onChange={(value) => {
          setFieldValue("producers", value)
        }}
        clearable
        value={values.producers || []}
        loadInitialValues={(ids: number[]) =>
          Promise.all(
            ids?.map((id) => fetchNamedEntity(id).then((producer) => ({ label: producer.name, value: producer.id })))
          )
        }
        loadOptions={(inputValue: string) => {
          return fetchNamedEntitiesProducers({ search: inputValue }).then((producers) => {
            return producers.results?.map((producer) => ({
              label: producer.name,
              value: producer.id,
            }))
          })
        }}
      >
        <AsyncMultiSelectField.Add
          onClick={() => {
            modalActionsCreateNamedEntity.open("PRODUCER")
          }}
        />
      </AsyncMultiSelectField>
      <AsyncMultiSelectField
        label="Raccolte"
        layout="horizontal"
        placeholder="Seleziona una o più raccolte"
        onChange={(value) => {
          setFieldValue("collections", value)
        }}
        clearable
        value={values.collections || []}
        loadInitialValues={(ids: number[]) =>
          Promise.all(
            ids?.map((id) =>
              fetchCollection(id).then((collection) => ({ label: collection.name, value: collection.id }))
            )
          )
        }
        loadOptions={(inputValue: string) => {
          return fetchCollections({ search: inputValue }).then((collections) => {
            return collections.results?.map((collection) => ({
              label: collection.name,
              value: collection.id,
            }))
          })
        }}
      >
        <AsyncMultiSelectField.Add
          onClick={() => {
            modalActionsCreateCollection.open()
          }}
        />
      </AsyncMultiSelectField>
      <CharField
        label="Anno"
        layout="horizontal"
        value={values.year ? values.year.toString() : ""}
        onChange={(value) => {
          setFieldValue("year", value ? Number(value) : undefined)
        }}
        isValid={submitCount > 0 ? !getIn(errors, "year") : undefined}
        errorMessage={getIn(errors, "year")}
        required
      />
      <CharField
        label="Data generica"
        layout="horizontal"
        value={values.date_generic || ""}
        onChange={(value) => {
          setFieldValue("date_generic", value)
        }}
        isValid={submitCount > 0 ? !getIn(errors, "date_generic") : undefined}
        errorMessage={getIn(errors, "date_generic")}
      />
      <CharField
        label="Anno evento"
        layout="horizontal"
        value={values.event_year ? values.event_year.toString() : ""}
        onChange={(value) => {
          setFieldValue("event_year", value ? Number(value) : undefined)
        }}
        isValid={submitCount > 0 ? !getIn(errors, "event_year") : undefined}
        errorMessage={getIn(errors, "event_year")}
      />
      <AsyncMultiSelectField
        label="Archivi"
        layout="horizontal"
        placeholder="Seleziona uno o più archivi"
        onChange={(value) => {
          setFieldValue("archives", value)
        }}
        clearable
        value={values.archives || []}
        loadInitialValues={(ids: number[]) =>
          Promise.all(
            ids?.map((id) => fetchNamedEntity(id).then((archive) => ({ label: archive.name, value: archive.id })))
          )
        }
        loadOptions={(inputValue: string) => {
          return fetchNamedEntitiesArchives({ search: inputValue }).then((archives) => {
            return archives.results?.map((archive) => ({
              label: archive.name,
              value: archive.id,
            }))
          })
        }}
      >
        <AsyncMultiSelectField.Add
          onClick={() => {
            modalActionsCreateNamedEntity.open("ARCHIVE")
          }}
        />
      </AsyncMultiSelectField>
      <LinkField
        label="Link embed"
        layout="horizontal"
        value={values.embed_url || ""}
        onChange={(value) => {
          setFieldValue("embed_url", value)
        }}
        isValid={submitCount > 0 ? !getIn(errors, "embed_url") : undefined}
        errorMessage={getIn(errors, "embed_url")}
      />
      {values.embed_url && (
        <Row>
          <Col md={4} className="fw-bold">
            Anteprima embed:
          </Col>
          <Col md={8}>
            {/* iframe for youtube video or vimeo checking the embed_url */}
            {values.embed_url.includes("youtube.com") && values.embed_url && (
              <iframe
                width="350"
                height="200"
                src={values.embed_url.replace("watch?v=", "embed/").split("&")[0]}
                title="Embedded video"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            )}
            {values.embed_url.includes("vimeo.com") && values.embed_url && (
              <iframe
                width="350"
                height="200"
                src={values.embed_url.replace("vimeo.com/", "player.vimeo.com/video/")}
                title="Embedded video"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            )}
            {values.embed_url.includes("facebook.com") && values.embed_url && (
              <iframe
                src={`https://www.facebook.com/plugins/video.php?height=448&href=${values?.embed_url}&show_text=false&width=560&t=0`}
                width="560"
                height="448"
                style={{ border: "none", overflow: "hidden", width: "100%", maxHeight: 400 }}
                allowFullScreen={true}
                allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
              ></iframe>
            )}
            {values.embed_url.includes("ok.ru") && values.embed_url && (
              <iframe
                src={`${values.embed_url}`}
                width="560"
                height="448"
                style={{ border: "none", overflow: "hidden", width: "100%", maxHeight: 400 }}
                allowFullScreen={true}
                allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
              ></iframe>
            )}
          </Col>
        </Row>
      )}
      <FieldArray name="source_urls">
        {({ push, remove }) => (
          <>
            {(values.source_urls || []).map((source_url, index) => (
              <>
                <LinkField
                  layout="horizontal"
                  label="Link fonte"
                  inputColSize={6}
                  value={source_url}
                  onChange={(value) => {
                    setFieldValue(`source_urls.${index}`, value)
                  }}
                  isValid={submitCount > 0 ? !getIn(errors, `source_urls.${index}`) : undefined}
                  errorMessage={getIn(errors, `source_urls.${index}`)}
                />
                <Row>
                  <Col md={4} />
                  <Col md={8}>
                    <button
                      type="button"
                      style={{ width: "fit-content" }}
                      className="btn btn-danger"
                      onClick={() => {
                        remove(index)
                      }}
                    >
                      Rimuovi
                    </button>
                  </Col>
                </Row>
              </>
            ))}
            <Row>
              <Col md={4} />
              <Col md={8}>
                <button
                  type="button"
                  className="btn btn-primary mt-2"
                  onClick={() => {
                    push("")
                  }}
                >
                  Aggiungi link di fonte
                </button>
              </Col>
            </Row>
          </>
        )}
      </FieldArray>

      <AsyncMultiSelectField
        label="Comuni"
        layout="horizontal"
        placeholder="Seleziona uno o più comuni"
        onChange={(value) => {
          setFieldValue("municipalities", value)
        }}
        clearable
        value={values.municipalities || []}
        loadInitialValues={(ids: number[]) =>
          Promise.all(
            ids?.map((id) =>
              fetchMunicipality(id).then((municipality) => ({ label: municipality.name, value: municipality.id }))
            )
          )
        }
        loadOptions={(inputValue: string) => {
          return fetchMunicipalities({ search: inputValue }).then((municipalities) => {
            return municipalities.results?.map((municipality) => ({
              label: municipality.name,
              value: municipality.id,
            }))
          })
        }}
      />
      <SwitchField
        label="Bozza"
        layout="horizontal"
        value={values.draft || false}
        onChange={(checked) => {
          setFieldValue("draft", checked)
        }}
      />

      {modalStateCreateCollection.isOpen && (
        <CollectionModal
          show={modalStateCreateCollection.isOpen}
          onClose={modalActionsCreateCollection.close}
          save={(valuesCollection, actions) => {
            return createCollection
              .mutateAsync(valuesCollection)
              .then((collection) => {
                setFieldValue("collections", [...((values.collections as number[]) || []), collection.id])
                modalActionsCreateCollection.close()
              })
              .catch((e) => {
                actions.setErrors(transformErrorForForm(e))
              })
          }}
          title="Nuova raccolta"
        />
      )}

      {modalStateCreateNamedEntity.isOpen && (
        <NamedEntityModal
          show={modalStateCreateNamedEntity.isOpen}
          onClose={modalActionsCreateNamedEntity.close}
          save={(valuesEntity, actions) => {
            return createNamedEntity
              .mutateAsync(valuesEntity)
              .then((namedEntity) => {
                if (valuesEntity.entity_type === "EDITOR")
                  setFieldValue("editors", [...((values.editors as number[]) || []), namedEntity.id])
                else if (valuesEntity.entity_type === "PRODUCER")
                  setFieldValue("producers", [...((values.producers as number[]) || []), namedEntity.id])
                else if (valuesEntity.entity_type === "AUTHOR")
                  setFieldValue("authors", [...((values.authors as number[]) || []), namedEntity.id])
                else if (valuesEntity.entity_type === "ARCHIVE") {
                  setFieldValue("archives", [...((values.archives as number[]) || []), namedEntity.id])
                }
                modalActionsCreateNamedEntity.close()
              })
              .catch((e) => {
                actions.setErrors(transformErrorForForm(e))
              })
          }}
          title="Nuova entità"
        />
      )}
      {modalStateCreateGenre.isOpen && (
        <GenreModal
          show={modalStateCreateGenre.isOpen}
          onClose={modalActionsCreateGenre.close}
          save={(values, actions) => {
            return createGenre
              .mutateAsync(values)
              .then((genre) => {
                setFieldValue("genre", genre.id)
                modalActionsCreateGenre.close()
              })
              .catch((e) => {
                actions.setErrors(transformErrorForForm(e))
              })
          }}
          title="Nuovo genere"
        />
      )}
    </Col>
  )
}
